/**
 * Centralized token management functions.
 *
 * Single source of truth for reading/writing auth tokens in localStorage.
 * Extracted to avoid duplication across auth.store, api-client, and websocket modules.
 * This module has ZERO imports from other app modules, so it can be safely
 * imported by any file without creating circular dependencies.
 */

/**
 * Retrieves the stored access token from localStorage.
 */
export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('token');
}

/**
 * Stores the access token in localStorage.
 */
export function setAccessToken(token: string): void {
  if (!token) return;
  localStorage.setItem('token', token);
}

/**
 * Removes the access token from localStorage.
 */
export function removeAccessToken(): void {
  localStorage.removeItem('token');
}

/**
 * Retrieves the stored refresh token from localStorage.
 */
export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('refreshToken');
}

/**
 * Stores the refresh token in localStorage.
 */
export function setRefreshToken(token: string): void {
  localStorage.setItem('refreshToken', token);
}

/**
 * Removes the refresh token from localStorage.
 */
export function removeRefreshToken(): void {
  localStorage.removeItem('refreshToken');
}

/**
 * Removes all auth-related items from localStorage.
 */
export function clearAuthStorage(): void {
  removeAccessToken();
  removeRefreshToken();
  localStorage.removeItem('user');
  localStorage.removeItem('tokenExpiry');
  localStorage.removeItem('activeOrgId');
  localStorage.removeItem('defaultOrgId');
}
